import classNames from 'classnames';
import WebChat from '../WebChat';
import Faqs from '../Faqs';
import RobotBubble from '../../icons/RobotBubble';
import Footer from '../Footer';
import Header from '../Header';
import styled from '@emotion/styled';
import isPropValid from '@emotion/is-prop-valid'
import Spinner from '../Spinner';
import locales from '../../locales';
import { Container } from '../../styledComponents';
import { useWebChat } from '../../context';
import { use100vh } from 'react-div-100vh'

const MinimizeBox = styled.div`
  position: relative;

  & > button.maximize > .red-dot {
    color: #ef0000;
    font-size: 0.875em;
    position: absolute;
    right: 0.75em;
    top: 0.75em;
  }

  & > button.maximize:focus:not(:active),
  & > button.maximize:hover:not(:active) {
    background-color: #069;
    border-color: #069;
  }

  & > button.maximize:active {
    background-color: White;
    color: #39c;
  }

  & > .chat-box.hide {
    display: none;
  }

  & > .chat-box.right {
    right: 0px;
  

    @media screen and (min-width: 768px) and (min-height: 768px) {
      right: 30px;
      left: auto;
    }

    @media screen and (min-width: 1024px) and (min-height: 1024px) {
      right: 60px;
      left: auto;
    }
  }

  & > .chat-box.bottom {
    bottom: 0;
    top: 0;
    margin: auto;

    @media screen and (min-width: 768px) and (min-height: 768px) {
      bottom: 1em;
      top: auto;
    }

    @media screen and (min-width: 1024px) and (min-height: 1024px) {
      bottom: 2em;
      top: auto;
    }
  }

  & > .chat-box > header > .filler {
    flex: 0 10000 100%;
  }

  & > .chat-box > header > button:focus:not(:active),
  & > .chat-box > header > button:hover:not(:active) {
    color: rgba(255, 255, 255, 0.6);
  }

  & > .chat-box > header > button:focus:active {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const ChatWrapper = styled.div`
  max-width: none;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(10px);
  box-shadow: 0 0 1.25em rgba(0, 0, 0, 0.2);
  bottom: 1.25em;
  display: flex;
  flex-direction: column;
  position: fixed;
  top: auto;
  bottom: auto;
  border-radius: 0;
  overflow: hidden;
  background: none;
  border: none;
  z-index: 1000;

  @media screen and (min-width: 640px) and (min-height: 768px) {
    border-radius: var(--chat-border-radius);
    max-width: var(--chat-container-width);
    max-height: var(--chat-container-height);
  }

  @media screen and (min-width: 1024px) {
    border-radius: var(--chat-border-radius);
    max-width: var(--chat-container-width);
    max-height: var(--chat-container-height);
  }

  & > div {
    height: 100%;
    min-height: 0;
  }
`;

const BubbleButton = styled.button`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 2.875em;
  width: 14.875em;
  border-radius: 50px;
  padding: 0.688em 1em 0.688em 3.438em;
  background-color: var(--color-primary);
  border: none;
  position: fixed;
  z-index: 1000;
  transition: background-color 0.2s ease-in-out;
  cursor: pointer;
  right: 0.25em;
  bottom: 1.5em;

  @media screen and (min-width: 768px) and (min-height: 768px) {
    right: 30px;
    bottom: 2em;
  }

  @media screen and (min-width: 1024px) and (min-height: 1024px) {
    right: 60px;
    bottom: 2em;
  }

  &:hover {
    background-color: var(--color-primary-dark) !important;
  }
`;

const SpanText = styled.span`
  font-weight: bold;
  font-size: 1em;
  line-height: 1.5em;
  color: var(--color-white);
  white-space: nowrap;
`;

const SpanIcon = styled.span`
  position: absolute;
  left: 0%;
  transform: translateX(-50%);
  height: 100px;
  width: 100px;
  left: 0px;
  top: 0px;
  border-radius: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  top: 0;
  bottom:0;
  margin: auto 0;
`;

const ChatContainer = styled(Container, {
  shouldForwardProp: prop =>
    isPropValid(prop) && prop !== 'display'
})(({ display }) => ({
  display
}))

const StatusIcon = styled.span`
  height: 0.625em;
  width: 0.625em;
  border-radius: 100%;
  background: #219653;
  transform: matrix(1, 0, 0, -1, 0, 0);
  position: absolute;
  left: 28px;
  top: -6px;
  z-index: 5;
`;

const StatusIconRed = styled(StatusIcon)`
  background: #e96e2c;
`;

const Bubble = ({ qnaUrl = '', kn = '', lang = 'fr-ca' }) => {
  const {
    minimized,
    loaded,
    newMessage,
    handleMaximizeButtonClick,
    activeTab,
  } = useWebChat();

  const isDesktopWidth = window.matchMedia('(min-width: 640px)');
  const isDesktopHeight = window.matchMedia('(min-height: 640px) and (min-width: 1024px)')
  const height = use100vh()
  const adjustedHeight = isDesktopWidth.matches && isDesktopHeight.matches ? (height * 0.96) : height;

  return (
    <MinimizeBox className="minimizable-web-chat">
      {minimized && (
        <BubbleButton className="maximize" onClick={handleMaximizeButtonClick}>
          <SpanIcon><RobotBubble /></SpanIcon>
          {newMessage ? <StatusIconRed /> : <StatusIcon />}
          <SpanText>{locales(lang, 'askYourQuestion')}</SpanText>
        </BubbleButton>
      )}
      {loaded && (
        <ChatWrapper style={{ height: adjustedHeight }} className={classNames('chat-box right bottom', minimized ? 'hide' : '')}>
          <Header />
          <ChatContainer display={activeTab ? 'block' : 'none'}>
            <WebChat className="react-web-chat" />
          </ChatContainer>

          {
            activeTab === false && <Faqs qnaUrl={qnaUrl} lang={lang} kn={kn} />
          }

          <Footer />
          <Spinner />
        </ChatWrapper>
      )}
    </MinimizeBox>
  );
};

export default Bubble;
